/**
 * 체험단 캠페인 상세 페이지 (고객사용)
 *
 * 캠페인 정보, 신청자 관리, 미션 관리를 탭으로 제공합니다.
 * 각 탭은 detail/ 디렉토리의 개별 컴포넌트로 분리되어 있습니다.
 *
 * Orval 자동생성 TanStack Query 훅 사용:
 * - useGetCampaign / useGetApplications / useGetSubmissions (데이터 조회)
 * - usePublishCampaign / useDeleteCampaign / useArchiveCampaign 등 (상태 변경)
 * - useBulkSelectApplications / useBulkShortlistApplications 등 (일괄 처리)
 */
import toast from 'react-hot-toast'
import { useQueryClient } from '@tanstack/react-query'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { TipBox } from '@/components/guide/TipBox'
import { SeedingPreviewModal } from '@/features/seeding/SeedingPreviewModal'
import { SeedingPublishGate, extractGateMessage } from '@/features/seeding/SeedingPublishGate'
import { CampaignTimelineBar } from '@/features/seeding/components/CampaignTimelineBar'
import { seedingApi } from '@/lib/api'
import { auth } from '@/lib/auth'
import { useShop } from '@/lib/shopContext'
import { buildPreviewDataFromCampaign } from '@/lib/seedingPreviewUtils'
import type {
  SeedingApplicationResponse,
  SeedingCampaignResponse,
  MissionSubmissionResponse,
} from '@/types'
import {
  useGetCampaign,
  useGetApplications,
  useGetSubmissions,
  usePublishCampaign,
  useDeleteCampaign,
  useArchiveCampaign,
  useUnarchiveCampaign,
  useUpdateCampaign,
  useBulkSelectApplications,
  useBulkShortlistApplications,
  useBulkCancelShortlist,
  useUpdateSubmission,
  getGetCampaignQueryKey,
  getGetApplicationsQueryKey,
  getGetSubmissionsQueryKey,
  getListCampaignsQueryKey,
} from '@/api/generated/v2-seeding-campaigns/v2-seeding-campaigns'
import { Link, Outlet, createFileRoute, useLocation, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  Archive,
  Check,
  Copy,
  Eye,
  ExternalLink,
  MoreVertical,
  Trash2,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useConfirm } from '@/hooks/useConfirm'

// 분할된 탭 컴포넌트 import (features/ 디렉토리로 이동됨)
import type { TabType } from '@/features/seeding/detail/constants'
import { statusBadgeVariant, statusLabel } from '@/features/seeding/detail/constants'
import { CampaignInfoTab, StatusGuideBanner, CampaignSummaryBar } from '@/features/seeding/detail/CampaignInfoTab'
import { CsvImportDialog } from '@/features/seeding/detail/CsvImportDialog'
import { ApplicationsTab } from '@/features/seeding/detail/ApplicationsTab'
import { ReviewsTab } from '@/features/seeding/detail/ReviewsTab'

// URL search params로 탭/필터 상태 관리
interface CampaignSearchParams {
  tab?: 'info' | 'applications' | 'missions'
  view?: 'all' | 'shortlisted' | 'selected' | 'cancelled'
  round?: number
}

export const Route = createFileRoute('/_dashboard/seeding-campaigns/$campaignId')({
  component: CampaignDetail,
  validateSearch: (search: Record<string, unknown>): CampaignSearchParams => {
    const tab = search.tab as string | undefined
    const validTabs = ['info', 'applications', 'missions']
    const view = search.view as string | undefined
    const validViews = ['all', 'shortlisted', 'selected']
    return {
      tab: tab && validTabs.includes(tab) ? (tab as CampaignSearchParams['tab']) : undefined,
      view: view && validViews.includes(view) ? (view as CampaignSearchParams['view']) : undefined,
      round: typeof search.round === 'number' ? search.round : undefined,
    }
  },
})

function CampaignDetail() {
  const { campaignId } = Route.useParams()
  const { tab, view, round } = Route.useSearch()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  // ==================== Shop 정보 + 인증 확인 ====================

  // shop 은 레이아웃이 useGetShop 으로 채운 컨텍스트에서 읽는다 (여기서 재조회하지 않는다)
  const { shop } = useShop()

  // 인증 확인
  useEffect(() => {
    if (!auth.isAuthenticated()) {
      navigate({ to: '/' })
    }
  }, [navigate])

  // 캠페인 상세 조회
  const campaignQuery = useGetCampaign(campaignId)
  const campaign = campaignQuery.data as SeedingCampaignResponse | undefined

  // 신청자 목록 조회 (limit: 5000)
  const applicationsQuery = useGetApplications(campaignId, { limit: 5000 })
  const applications: SeedingApplicationResponse[] =
    ((applicationsQuery.data as { applications?: SeedingApplicationResponse[] } | undefined)
      ?.applications as SeedingApplicationResponse[]) ?? []

  // 미션 제출 목록 조회 (limit: 200)
  const submissionsQuery = useGetSubmissions(campaignId, { limit: 200 })
  const submissions: MissionSubmissionResponse[] =
    ((submissionsQuery.data as { submissions?: MissionSubmissionResponse[] } | undefined)
      ?.submissions as MissionSubmissionResponse[]) ?? []

  const isLoading = campaignQuery.isLoading || applicationsQuery.isLoading || submissionsQuery.isLoading
  const error = campaignQuery.error || applicationsQuery.error || submissionsQuery.error

  // ==================== UI 상태 ====================

  const [showPreview, setShowPreview] = useState(false)
  // 공개 게이트 안내 메시지 (백엔드 422 detail). null이면 안내 다이얼로그를 닫아둔다.
  const [gateMessage, setGateMessage] = useState<string | null>(null)
  const [tabInitialized, setTabInitialized] = useState(false)
  const [selectedAppIds, setSelectedAppIds] = useState<string[]>([])
  const [urlCopied, setUrlCopied] = useState(false)
  const [csvImportOpen, setCsvImportOpen] = useState(false)
  const [csvImportFile, setCsvImportFile] = useState<File | null>(null)
  const csvFileInputRef = useRef<HTMLInputElement>(null)
  const { confirm, ConfirmDialog } = useConfirm()

  const activeTab: TabType = tab || 'info'

  // 신청 페이지 URL
  const getApplyUrl = () => {
    if (!shop) return ''
    const seedingAppUrl = import.meta.env.VITE_SEEDING_APP_URL
    if (campaign?.short_code && seedingAppUrl) {
      return `${seedingAppUrl}/${campaign.short_code}`
    }
    return `${window.location.origin}/apply/${shop.cafe24_mall_id}/${shop.shop_no}/${campaignId}`
  }

  // 더보기 메뉴
  const [showMoreMenu, setShowMoreMenu] = useState(false)
  const moreMenuRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!showMoreMenu) return
    const handler = (e: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setShowMoreMenu(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showMoreMenu])

  // URL 복사
  const handleCopyUrl = async () => {
    if (!shop) return
    const applyUrl = getApplyUrl()
    try {
      await navigator.clipboard.writeText(applyUrl)
      setUrlCopied(true)
      setTimeout(() => setUrlCopied(false), 2000)
    } catch {
      const textArea = document.createElement('textarea')
      textArea.value = applyUrl
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setUrlCopied(true)
      setTimeout(() => setUrlCopied(false), 2000)
    }
  }

  // 기본 탭 자동 선택
  useEffect(() => {
    if (!campaign || tabInitialized || tab) return
    if (location.pathname !== `/seeding-campaigns/${campaignId}`) return
    const getDefaultTab = (status: string): TabType => {
      if (status === 'draft' || status === 'scheduled' || status === 'completed') return 'info'
      if (status === 'recruiting' || status === 'pending_selection') return 'applications'
      return applications.some((a) => a.status === 'selected') ? 'missions' : 'applications'
    }
    const defaultTab = getDefaultTab(campaign.status)
    if (defaultTab !== 'info') {
      navigate({
        to: '/seeding-campaigns/$campaignId',
        params: { campaignId },
        search: { tab: defaultTab },
        replace: true,
      })
    }
    setTabInitialized(true)
  }, [campaign, applications, tabInitialized, tab, navigate])

  // 자식 라우트 활성화 시 Outlet 렌더링
  if (location.pathname !== `/seeding-campaigns/${campaignId}`) {
    return <Outlet />
  }

  // ==================== Orval Mutation 훅 ====================

  // 캠페인/신청자/미션 관련 쿼리 무효화 헬퍼 (목록도 함께 갱신)
  const invalidateCampaignQueries = () => {
    queryClient.invalidateQueries({ queryKey: getGetCampaignQueryKey(campaignId) })
    queryClient.invalidateQueries({ queryKey: getGetApplicationsQueryKey(campaignId, { limit: 5000 }) })
    queryClient.invalidateQueries({ queryKey: getGetSubmissionsQueryKey(campaignId, { limit: 200 }) })
    queryClient.invalidateQueries({ queryKey: getListCampaignsQueryKey() })
  }

  const publishMutation = usePublishCampaign()
  const deleteMutation = useDeleteCampaign()
  const archiveMutation = useArchiveCampaign()
  const unarchiveMutation = useUnarchiveCampaign()
  const updateCampaignMutation = useUpdateCampaign()
  const bulkSelectMutation = useBulkSelectApplications()
  const bulkShortlistMutation = useBulkShortlistApplications()
  const bulkCancelShortlistMutation = useBulkCancelShortlist()
  const updateSubmissionMutation = useUpdateSubmission()

  // ==================== 액션 핸들러 ====================

  const handlePublish = async () => {
    if (!campaign) return
    const ok = await confirm('캠페인을 공개하시겠습니까?\n공개 후 신청을 받을 수 있습니다.')
    if (!ok) return
    try {
      await publishMutation.mutateAsync({ campaignId })
      invalidateCampaignQueries()
      toast.success('캠페인이 공개되었어요.')
    } catch (err: unknown) {
      // 연락처 미등록 게이트(422)면 안내 다이얼로그로 등록을 유도한다
      const gateMsg = extractGateMessage(err)
      if (gateMsg) {
        setGateMessage(gateMsg)
        return
      }
      // 그 외 에러는 기존처럼 토스트로 안내
      const errObj = err as { detail?: string; message?: string }
      toast.error(errObj?.detail || errObj?.message || '공개에 실패했어요.')
    }
  }

  const handleSelectApplications = async () => {
    if (selectedAppIds.length === 0) { toast.error('선정할 신청자를 선택해주세요.'); return }
    const currentSelected = applications.filter(a => a.status === 'selected').length
    const willBeTotal = currentSelected + selectedAppIds.length
    // 모집 인원 미입력(임시 저장)이면 초과 개념이 없어 정원 확인을 건너뛴다
    if (campaign?.recruit_count != null && willBeTotal > campaign.recruit_count) {
      const overflow = willBeTotal - campaign.recruit_count
      const ok = await confirm(`정원(${campaign!.recruit_count}명)을 ${overflow}명 초과합니다.\n선정을 진행하면 정원이 ${willBeTotal}명으로 자동 변경됩니다.\n\n선정 대상자에게 선정 알림톡이 발송됩니다.\n계속하시겠습니까?`)
      if (!ok) return
      try { await updateCampaignMutation.mutateAsync({ campaignId, data: { recruit_count: willBeTotal } }) } catch { /* 계속 진행 */ }
    } else {
      const ok2 = await confirm(`${selectedAppIds.length}명을 최종 선정하시겠습니까?\n선정 대상자에게 선정 알림톡이 발송됩니다.`)
      if (!ok2) return
    }
    try {
      await bulkSelectMutation.mutateAsync({ campaignId, data: { application_ids: selectedAppIds } })
      setSelectedAppIds([])
      invalidateCampaignQueries()
      toast.success('선정이 완료되었어요.')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '선정 처리에 실패했어요.'
      toast.error(msg)
    }
  }

  const handleShortlistApplications = async () => {
    if (selectedAppIds.length === 0) { toast.error('후보로 선정할 신청자를 선택해주세요.'); return }
    try {
      await bulkShortlistMutation.mutateAsync({ campaignId, data: { application_ids: selectedAppIds } })
      setSelectedAppIds([])
      invalidateCampaignQueries()
      toast.success('후보로 선정되었어요.')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '후보 선정에 실패했어요.'
      toast.error(msg)
    }
  }

  const handleCancelShortlistApplications = async () => {
    if (selectedAppIds.length === 0) { toast.error('후보 취소할 신청자를 선택해주세요.'); return }
    try {
      await bulkCancelShortlistMutation.mutateAsync({ campaignId, data: { application_ids: selectedAppIds } })
      setSelectedAppIds([])
      invalidateCampaignQueries()
      toast.success('후보 선정이 취소되었어요.')
      // 후보 전원 취소 시 전체 뷰로 이동
      const refreshedApps = applicationsQuery.data
      const shortlistedCount = ((refreshedApps as { applications?: SeedingApplicationResponse[] } | undefined)?.applications ?? [])
        .filter((a: SeedingApplicationResponse) => a.status === 'shortlisted').length
      if (shortlistedCount === 0) {
        navigate({ to: '/seeding-campaigns/$campaignId', params: { campaignId }, search: { tab: 'applications' as const, view: 'all' }, replace: true })
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '후보 취소에 실패했어요.'
      toast.error(msg)
    }
  }

  const handleSeedingStatus = async (submissionId: string, status: 'approved' | 'rejected', rejectionReason?: string) => {
    try {
      await updateSubmissionMutation.mutateAsync({
        submissionId,
        data: { status, ...(rejectionReason && { rejection_reason: rejectionReason }) },
      })
      invalidateCampaignQueries()
      if (status === 'approved') {
        toast.success('미션이 승인되었어요.')
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '미션 상태 변경에 실패했어요.'
      toast.error(msg)
    }
  }

  const handleDelete = async () => {
    if (!campaign || campaign.status !== 'draft') return
    const ok = await confirm('이 캠페인을 삭제하시겠습니까? 삭제 후 복구할 수 없어요.', { variant: 'danger' })
    if (!ok) return
    try {
      await deleteMutation.mutateAsync({ campaignId })
      // 목록 캐시 무효화 (삭제된 캠페인이 목록에서 즉시 제거되도록)
      queryClient.invalidateQueries({ queryKey: getListCampaignsQueryKey() })
      navigate({ to: '/seeding-campaigns' })
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '캠페인 삭제에 실패했어요.'
      toast.error(msg)
    }
  }

  const handleArchive = async () => {
    if (!campaign) return
    if (campaign.selected_count > 0) { toast.error('선정자가 있는 캠페인은 아카이브할 수 없어요.'); return }
    const ok = await confirm('이 캠페인을 아카이브하시겠습니까?\n신청 페이지가 비공개 처리되고, 목록에서 숨겨집니다.', { variant: 'danger' })
    if (!ok) return
    try {
      await archiveMutation.mutateAsync({ campaignId })
      invalidateCampaignQueries()
      toast.success('캠페인이 아카이브되었어요.')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '캠페인 아카이브에 실패했어요.'
      toast.error(msg)
    }
  }

  const handleUnarchive = async () => {
    if (!campaign) return
    const ok = await confirm('아카이브를 해제하시겠습니까?\n신청 페이지가 다시 공개됩니다.')
    if (!ok) return
    try {
      await unarchiveMutation.mutateAsync({ campaignId })
      invalidateCampaignQueries()
      toast.success('아카이브가 해제되었어요.')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '아카이브 해제에 실패했어요.'
      toast.error(msg)
    }
  }

  const handleDuplicate = () => {
    if (!campaign) return
    navigate({ to: '/seeding-campaigns/new', state: { duplicateFrom: campaign } as unknown as Record<string, string> })
  }

  // CSV 내보내기 — Blob 다운로드라 seedingApi 유지 (Orval customInstance는 JSON 파싱)
  const handleExport = async (type: 'selected' | 'all') => {
    try {
      const blob = type === 'all'
        ? await seedingApi.exportAllApplications(campaignId)
        : await seedingApi.exportSelectedApplications(campaignId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${campaign?.title}_${type}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '다운로드에 실패했어요.'
      toast.error(msg)
    }
  }

  // 데이터 리페치 콜백 (탭 컴포넌트에 전달)
  const refreshData = async () => {
    await queryClient.invalidateQueries({ queryKey: getGetApplicationsQueryKey(campaignId, { limit: 5000 }) })
    await queryClient.invalidateQueries({ queryKey: getGetCampaignQueryKey(campaignId) })
  }

  // ==================== 렌더링 ====================

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-surface-high rounded w-64 mb-6"></div>
          <div className="h-96 bg-surface-high rounded"></div>
        </div>
      </div>
    )
  }

  if (error || !campaign) {
    return (
      <div className="text-center py-12">
        <span className="text-4xl block mb-4">&#9888;&#65039;</span>
        <div className="text-on-surface-variant text-lg">{(error as Error)?.message || '캠페인을 찾을 수 없어요.'}</div>
        <Button className="mt-4" onClick={() => navigate({ to: '/seeding-campaigns' })}>목록으로</Button>
      </div>
    )
  }

  return (
    <>
      {/* 신청자 탭은 사이드 패널을 위한 가로 공간이 필요해 폭을 확장한다 */}
      <div className={`mx-auto ${activeTab === 'applications' ? 'max-w-7xl' : 'max-w-5xl'}`}>
        {/* Header */}
        <div className="mb-6">
          {/* 상단: 뒤로 + 타이틀 + 버튼 */}
          <div className="flex items-start gap-3 sm:gap-4">
            <Link to="/seeding-campaigns" className="text-on-surface-variant hover:text-on-surface mt-1 shrink-0">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <Badge variant={statusBadgeVariant[campaign.status]}>{statusLabel[campaign.status]}</Badge>
              </div>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <h1 className="text-xl font-bold text-on-surface break-words">{campaign.title}</h1>
                  {/* 임시 저장 캠페인은 상품이 아직 없을 수 있다 — 빈 줄 대신 미선택으로 알린다 */}
                  <p className="mt-1 text-on-surface-variant">
                    {campaign.product_name || '상품 미선택'}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0 flex-wrap">
            <Button variant="outline" onClick={() => navigate({ to: '/seeding-campaigns' })}>목록</Button>
            <Button variant="outline" onClick={() => setShowPreview(true)}>
              <Eye className="h-4 w-4 mr-1" />미리보기
            </Button>
            {campaign.status !== 'completed' && campaign.status !== 'archived' && (
              <Button variant="outline" onClick={() => navigate({ to: '/seeding-campaigns/$campaignId/edit', params: { campaignId } })}>수정</Button>
            )}
            {campaign.status === 'draft' && <Button onClick={handlePublish}>공개하기</Button>}
            {/* 더보기 메뉴 */}
            <div className="relative" ref={moreMenuRef}>
              <button
                onClick={() => setShowMoreMenu(!showMoreMenu)}
                className="inline-flex items-center justify-center h-[36px] w-[36px] sm:h-[38px] sm:w-[38px] min-h-[44px] sm:min-h-0 min-w-[44px] sm:min-w-0 border border-border bg-transparent text-on-surface hover:bg-surface rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
              {showMoreMenu && (
                <div className="absolute right-0 mt-1 w-44 bg-surface-lowest rounded-lg shadow-md border border-border py-1 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
                  <button className="w-full text-left px-4 py-2 text-sm text-on-surface hover:bg-surface active:bg-surface-low flex items-center gap-2" onClick={() => { setShowMoreMenu(false); handleDuplicate() }}>
                    <Copy className="h-4 w-4 text-outline-variant" />복제
                  </button>
                  {campaign.status !== 'draft' && campaign.status !== 'archived' && (
                    <button className="w-full text-left px-4 py-2 text-sm text-on-surface hover:bg-surface active:bg-surface-low flex items-center gap-2" onClick={() => { setShowMoreMenu(false); handleArchive() }}>
                      <Archive className="h-4 w-4 text-outline-variant" />아카이브
                    </button>
                  )}
                  {campaign.status === 'archived' && (
                    <button className="w-full text-left px-4 py-2 text-sm text-on-surface hover:bg-surface active:bg-surface-low flex items-center gap-2" onClick={() => { setShowMoreMenu(false); handleUnarchive() }}>
                      <Archive className="h-4 w-4 text-outline-variant" />아카이브 해제
                    </button>
                  )}
                  {campaign.status === 'draft' && (
                    <button className="w-full text-left px-4 py-2 text-sm text-error hover:bg-error/10 active:bg-error/10 flex items-center gap-2" onClick={() => { setShowMoreMenu(false); handleDelete() }}>
                      <Trash2 className="h-4 w-4 text-error" />삭제
                    </button>
                  )}
                </div>
              )}
            </div>
              </div>
            </div>
          </div>
        </div>
        </div>

        {/* 공개 전 미완성 안내 — 무엇이 비었는지 서버(publish_blockers)가 알려준다.
            공개 버튼을 눌러 422 를 받기 전에 미리 보이도록 상단에 둔다. */}
        {campaign.status === 'draft' && (campaign.publish_blockers?.length ?? 0) > 0 && (
          <div className="mb-6">
            <TipBox type="warning">
              <p className="font-medium">공개하려면 아직 필요해요</p>
              <ul className="mt-2 list-disc list-inside space-y-0.5">
                {campaign.publish_blockers?.map((blocker) => (
                  <li key={blocker}>{blocker}</li>
                ))}
              </ul>
              <div className="mt-3">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate({
                    to: '/seeding-campaigns/$campaignId/edit',
                    params: { campaignId },
                  })}
                >
                  수정하러 가기
                </Button>
              </div>
            </TipBox>
          </div>
        )}

        {/* 요약바 + 신청 페이지 링크 */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <CampaignSummaryBar
            campaign={campaign}
            applications={applications}
            submissions={submissions}
            onNavigate={(newTab) => navigate({ to: '/seeding-campaigns/$campaignId', params: { campaignId }, search: { tab: newTab } })}
          />
          {(() => {
            const applyUrl = campaign.status !== 'draft' && shop ? getApplyUrl() : null
            if (!applyUrl) return null
            return (
              <div className="inline-flex items-center gap-1.5 rounded-lg bg-primary/10 ring-1 ring-primary/20 pl-3 pr-1 py-1.5 max-w-full">
                <ExternalLink className="w-3.5 h-3.5 text-primary/60 shrink-0" />
                <span className="text-xs text-primary/70 shrink-0 hidden sm:inline">신청 페이지 링크</span>
                <a href={applyUrl} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-primary hover:text-primary truncate min-w-0">{applyUrl}</a>
                <button onClick={handleCopyUrl} className="shrink-0 inline-flex items-center gap-1 ml-0.5 px-2 py-0.5 text-xs text-primary rounded-md hover:bg-primary/10 transition-colors">
                  {urlCopied ? (<><Check className="w-3 h-3 text-emerald-500" /> 복사됨</>) : (<><Copy className="w-3 h-3" /> 복사</>)}
                </button>
              </div>
            )
          })()}
        </div>

        {/* 타임라인 — 모바일에서는 공간 대비 가독성이 떨어져서 숨김 */}
        <div className="hidden sm:block">
          <CampaignTimelineBar
            recruitStartAt={campaign.recruit_start_at || undefined}
            recruitEndAt={campaign.recruit_end_at || undefined}
            selectionAnnounceAt={campaign.selection_announce_at || undefined}
            roundDeadlines={(campaign.mission_rounds || []).map((r) => r.deadline_at ?? '')}
            campaignEndAt={campaign.campaign_end_at || undefined}
            sticky={false}
            readOnly
            label="캠페인 일정"
          />
        </div>

        <StatusGuideBanner status={campaign.status} />

        {/* 탭 */}
        <div className="border-b border-border mb-6">
          <nav className="flex gap-4 sm:gap-8 overflow-x-auto scrollbar-none">
            {(() => {
              const pendingCount = submissions.filter((s) => s.status === 'submitted').length
              const hasSelected = applications.some((a) => a.status === 'selected')
              const tabs = [
                { key: 'info', label: '캠페인 정보', badge: 0 },
                { key: 'applications', label: '참여자 관리', badge: 0 },
                ...(hasSelected ? [{ key: 'missions', label: '미션 관리', badge: pendingCount }] : []),
              ]
              return tabs.map((t) => (
                <button
                  key={t.key}
                  onClick={() => navigate({ to: '/seeding-campaigns/$campaignId', params: { campaignId }, search: { tab: t.key as TabType } })}
                  className={`pb-3 text-sm sm:text-base font-medium border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap shrink-0 ${
                    activeTab === t.key ? 'border-primary text-primary' : 'border-transparent text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  {t.label}
                  {t.badge > 0 && <span className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-red-500 text-white">{t.badge}</span>}
                </button>
              ))
            })()}
          </nav>
        </div>

        {/* 탭 콘텐츠 */}
        {activeTab === 'info' && <CampaignInfoTab campaign={campaign} />}

        {activeTab === 'applications' && (
          <ApplicationsTab
            campaign={campaign}
            applications={applications}
            selectedIds={selectedAppIds}
            onSelectionChange={setSelectedAppIds}
            onSelect={handleSelectApplications}
            onShortlist={handleShortlistApplications}
            onCancelShortlist={handleCancelShortlistApplications}
            onExport={() => handleExport('selected')}
            onExportAll={() => handleExport('all')}
            onImport={() => csvFileInputRef.current?.click()}
            onRefresh={refreshData}
            view={view ?? null}
            onViewChange={(newView: 'all' | 'shortlisted' | 'selected' | 'cancelled') => {
              setSelectedAppIds([])
              navigate({ to: '/seeding-campaigns/$campaignId', params: { campaignId }, search: { tab: 'applications' as const, view: newView || undefined }, replace: true })
            }}
          />
        )}

        {/* 숨겨진 파일 input */}
        <input
          ref={csvFileInputRef}
          type="file"
          accept=".csv,.txt,.tsv"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) { setCsvImportFile(file); setCsvImportOpen(true) }
            e.target.value = ''
          }}
        />

        {campaign && (
          <CsvImportDialog
            campaignId={campaign.id}
            open={csvImportOpen}
            initialFile={csvImportFile}
            onOpenChange={(open) => { setCsvImportOpen(open); if (!open) setCsvImportFile(null) }}
            onReselect={() => csvFileInputRef.current?.click()}
            onRefresh={refreshData}
            existingPhones={applications.map(a => a.phone)}
          />
        )}

        {activeTab === 'missions' && (
          <ReviewsTab
            campaign={campaign}
            submissions={submissions}
            applications={applications}
            onStatusChange={handleSeedingStatus}
            round={round ?? 0}
            onRoundChange={(newRound) => navigate({ to: '/seeding-campaigns/$campaignId', params: { campaignId }, search: { tab: 'missions' as const, round: newRound || undefined }, replace: true })}
          />
        )}
      </div>

      {/* 미리보기 모달 */}
      {showPreview && campaign && (
        <SeedingPreviewModal
          previewData={buildPreviewDataFromCampaign(campaign, shop ?? null)}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* 공개 게이트 안내 — 연락처 미등록 시 등록 유도 (닫으면 다시 공개 시도) */}
      <SeedingPublishGate message={gateMessage} onClose={() => setGateMessage(null)} />

      <ConfirmDialog />
    </>
  )
}
